<template>
    <div>
        <div class="course-panel-inline-grid">
                <div class="course-panel-inline-cell">
                    <fe-remote-combo
                        label="School Year"
                        v-model="selections.cohort_school_year_id"
                        url="qry.php?action=get&property=school_years"
                        rootProperty="years"
                        item-value="id"
                        item-text="name"
                        hide-details
                        dense
                        class="course-panel-inline-combo"
                        data-test="course-school-year-filter-field"
                    />
                </div>
                <div class="course-panel-inline-cell">
                    <fe-remote-combo
                        v-model="selections.course_id"
                        label="Courses"
                        :byId="false"
                        :items="inlineCourses"
                        itemText="course_name"
                        itemValue="course_id"
                        class="course-panel-inline-combo"
                        :selectAllOption="false"
                        :disabled="!inlineHasYearAndUser"
                        multiple
                        dense
                        lazyLoad
                        hide-details
                        data-test="course-courses-filter-field"
                    />
                </div>
                <div class="course-panel-inline-cell">
                    <fe-remote-combo
                        label="Teacher"
                        v-model="selections.user_id"
                        :items="inlineUsers"
                        item-text="user_full_name"
                        item-value="user_id"
                        :disabled="!selections.cohort_school_year_id"
                        clearable
                        hide-details
                        dense
                        class="course-panel-inline-combo"
                        data-test="course-user-filter-field"
                    />
                </div>
                <div class="course-panel-inline-cell">
                    <fe-remote-combo
                        v-model="selections.period"
                        label="Periods"
                        :byId="false"
                        :items="inlinePeriods"
                        :disabled="!inlineHasYearAndUser || !inlineHasCourse"
                        itemText="period"
                        data-test="course-periods-filter-field"
                        itemValue="period"
                        class="course-panel-inline-combo"
                        :selectAllOption="inlinePeriods.length > 0"
                        multiple
                        dense
                        lazyLoad
                        hide-details
                    />
                </div>
            </div>
    </div>
</template>

<script>
import * as Util from '../../Util'

export default {
    name: 'CoursePanel',
    rank: 1,

    props: {
        existingFilter: { type: Object, default: null },
    },

    data() {
        return {
            type: 'course',
            selections: {
                advancedFilters: {
                    student_active_flag: { id: 1 }
                }
            },
            inlineUsers: [],
            inlineCourses: [],
            inlinePeriods: [],
        }
    },

    computed: {
        enableFinalPanel() {
            return !this.$_.isEmpty(this.selections.course_id) && !this.$_.isEmpty(this.selections.period)
        },
        /** True when user has courses selected but no periods (invalid incomplete state). */
        hasIncompleteState() {
            return !this.$_.isEmpty(this.selections.course_id) && this.$_.isEmpty(this.selections.period)
        },
        inlineHasYearAndUser() {
            return !!(this.selections.cohort_school_year_id && this.selections.user_id)
        },
        inlineHasCourse() {
            return !!(this.selections.course_id && this.selections.course_id.length)
        },
        isDirty() {
            // Monitor form state to enable/disable apply button appropriately
            const applied = this.existingFilter?.editorParams?.selections
            if (!applied) return false
            const current = this.selections

            const appliedYearId = applied.cohort_school_year_id ?? null
            const currentYearId = current.cohort_school_year_id ?? null
            if (appliedYearId !== currentYearId) return true

            const appliedUserId = applied.user_id?.user_id ?? null
            const currentUserId = current.user_id?.user_id ?? null
            if (appliedUserId !== currentUserId) return true

            const appliedCourseIds = !Array.isArray(applied.course_id) ? [] : applied.course_id.map(x => x?.course_id ?? x).sort((a, b) => a - b)
            const currentCourseIds = !Array.isArray(current.course_id) ? [] : current.course_id.map(x => x?.course_id ?? x).sort((a, b) => a - b)
            if (appliedCourseIds.length !== currentCourseIds.length || appliedCourseIds.some((id, i) => id !== currentCourseIds[i])) return true

            const appliedPeriods = !Array.isArray(applied.period) ? [] : applied.period.map(x => x?.period ?? x).slice().sort()
            const currentPeriods = !Array.isArray(current.period) ? [] : current.period.map(x => x?.period ?? x).slice().sort()
            return appliedPeriods.length !== currentPeriods.length || appliedPeriods.some((p, i) => p !== currentPeriods[i])
        },
    },

    watch: {
        existingFilter: {
            handler(f) {
                // Sync form state from the applied filter when it changes; clear form when the filter is removed.
                if (f?.editorParams?.selections) {
                    this.selections = this.$_.cloneDeep(f.editorParams.selections)
                    // Load dropdown options so User/Courses/Periods lists are available when editing.
                    if (this.selections.cohort_school_year_id) this.loadInlineUsers()
                    if (this.selections.user_id) this.loadInlineCourses()
                    if (this.selections.course_id?.length) this.loadInlinePeriods()
                } else {
                    this.selections = {
                        advancedFilters: { student_active_flag: { id: 1 } },
                        cohort_school_year_id: null,
                        user_id: null,
                        course_id: [],
                        period: [],
                    }
                }
            },
            immediate: true,
        },
        'selections.cohort_school_year_id'(v) {
            if (v) {
                this.$nextTick(() => {
                    if (this.selections.cohort_school_year_id) this.loadInlineUsers()
                })
            } else {
                this.inlineUsers = []
                this.inlineCourses = []
                this.inlinePeriods = []
                this.$set(this.selections, 'user_id', null)
                this.$set(this.selections, 'course_id', [])
                this.$set(this.selections, 'period', [])
            }
        },
        inlineUsers: {
            handler(list) {
                const yearId = this.selections.cohort_school_year_id?.id ?? this.selections.cohort_school_year_id
                if (yearId && !list.length) this.loadInlineUsers()
            },
        },
        inlineCourses: {
            handler(list) {
                const yearId = this.selections.cohort_school_year_id?.id ?? this.selections.cohort_school_year_id
                const userId = this.selections.user_id?.user_id ?? this.selections.user_id
                if (yearId && userId && !list.length) this.loadInlineCourses()
            },
        },
        'selections.user_id'(newVal, oldVal) {
            if (newVal) {
                // Ensure user list is available (e.g. after clearing year then re-selecting, or when restoring from filter).
                if (!this.inlineUsers.length && this.selections.cohort_school_year_id) this.loadInlineUsers()
                // Clear courses and periods when user changes (different users have different courses).
                const userChanged = oldVal != null && (newVal?.user_id !== oldVal?.user_id)
                if (userChanged) {
                    this.inlineCourses = []
                    this.inlinePeriods = []
                    this.$set(this.selections, 'course_id', [])
                    this.$set(this.selections, 'period', [])
                }
                this.$nextTick(() => {
                    if (this.selections.user_id) this.loadInlineCourses()
                })
            } else {
                this.inlineCourses = []
                this.inlinePeriods = []
                this.$set(this.selections, 'course_id', [])
                this.$set(this.selections, 'period', [])
            }
        },
        'selections.course_id'(v) {
            if (v?.length) {
                this.$nextTick(() => {
                    if (this.selections.course_id?.length) this.loadInlinePeriods()
                })
            } else {
                this.inlinePeriods = []
                this.$set(this.selections, 'period', [])
                this.$emit('invalid-change', false)
                this.$emit('valid-change', false)
            }
        },
        inlinePeriods: {
            handler(list) {
                const yearId = this.selections.cohort_school_year_id?.id ?? this.selections.cohort_school_year_id
                const userId = this.selections.user_id?.user_id ?? this.selections.user_id
                const courseId = Array.isArray(this.selections.course_id)
                    ? this.selections.course_id.map(x => x?.course_id ?? x).filter(id => id != null && id !== '')
                    : []
                if (yearId && userId && courseId.length && !list.length) this.loadInlinePeriods()
            },
        },
        enableFinalPanel: {
            handler(v) {
                this.$emit('valid-change', v)
            },
            immediate: true,
        },
        hasIncompleteState: {
            handler(v) {
                this.$emit('invalid-change', v)
            },
            immediate: true,
        },
        isDirty: {
            handler(v) {
                this.$emit('dirty-change', v)
            },
            immediate: true,
        },
    },

    mounted() {
        this.loadDefaultSchoolYear()
        this.$emit('valid-change', this.enableFinalPanel)
    },
    methods: {
        loadDefaultSchoolYear() {
            this.$axios.get('qry.php?action=get&property=school_years').then((r) => {
                const years = r.data?.years || []
                if (years.length && !this.selections.cohort_school_year_id) {
                    this.$set(this.selections, 'cohort_school_year_id', years[0].id)
                }
            })
        },
        loadInlineUsers() {
            const yearId = this.selections.cohort_school_year_id?.id ?? this.selections.cohort_school_year_id
            if (!yearId) return
            this.$axios.get('courseHistory.php?action=get&property=user_id&' + this.$objectToParams({
                school_year_id: yearId,
                active_only: 1
            }))
                .then(r => {
                    const currentYearId = this.selections.cohort_school_year_id?.id ?? this.selections.cohort_school_year_id
                    if (currentYearId !== yearId) return
                    const list = r.data.courses || []
                    this.inlineUsers = [{ user_id: -1, user_full_name: 'All Users' }, ...list]
                })
        },
        loadInlineCourses() {
            const yearId = this.selections.cohort_school_year_id?.id ?? this.selections.cohort_school_year_id
            const userId = this.selections.user_id?.user_id ?? this.selections.user_id
            if (!yearId || !userId) return
            this.$axios.get('courseHistory.php?action=get&property=course_id&school_year_id=' + yearId + '&user_id=' + userId)
                .then(r => {
                    const currentYearId = this.selections.cohort_school_year_id?.id ?? this.selections.cohort_school_year_id
                    const currentUserId = this.selections.user_id?.user_id ?? this.selections.user_id
                    if (currentYearId !== yearId || currentUserId !== userId) return
                    this.inlineCourses = r.data.courses || []
                })
        },
        loadInlinePeriods() {
            const yearId = this.selections.cohort_school_year_id?.id ?? this.selections.cohort_school_year_id
            const userId = this.selections.user_id?.user_id ?? this.selections.user_id
            const raw = this.selections.course_id
            const courseIds = Array.isArray(raw)
                ? raw.map(x => x?.course_id ?? x).filter(id => id != null && id !== '')
                : (raw != null && raw !== '' ? [raw] : [])
            if (!yearId || !userId || !courseIds.length) return
            const courseParam = courseIds.join(',')
            this.$axios.get('courseHistory.php?action=get&property=period&school_year_id=' + yearId + '&user_id=' + userId + '&course_id=' + courseParam)
                .then(r => {
                    const currentYearId = this.selections.cohort_school_year_id?.id ?? this.selections.cohort_school_year_id
                    const currentUserId = this.selections.user_id?.user_id ?? this.selections.user_id
                    const currentIds = Array.isArray(this.selections.course_id)
                        ? this.selections.course_id.map(x => x?.course_id ?? x).filter(id => id != null && id !== '').sort((a, b) => a - b)
                        : []
                    const requestedIds = [...courseIds].sort((a, b) => a - b)
                    if (currentYearId !== yearId || currentUserId !== userId || currentIds.length !== requestedIds.length || requestedIds.some((id, i) => currentIds[i] !== id)) return
                    this.inlinePeriods = r.data.courses || []
                    if (this.selections.course_id?.length > 0) {
                        this.$set(this.selections, 'period', this.inlinePeriods)
                    }
                })
        },
        async loadPeriods() {
            const courseId = this.selections.course_id.map(x => x.course_id)
            const userId = this.selections.user_id?.user_id ?? this.selections.user_id
            return await this.$axios.get('courseHistory.php?action=get&property=period&school_year_id=' + this.selections.cohort_school_year_id + '&user_id=' + userId + '&course_id=' + courseId)
                .then(r => this.selections.periods = r.data.courses)
        },

        getFilter() {
            if (this.$_.isEmpty(this.selections.course_id) || this.$_.isEmpty(this.selections.period)) return null
            const uniqueId = Util.UUIDv4()
            const name = this.selections.course_id?.map(x => x.course_name).join(', ')
            let period = this.selections.period?.map(x => x.period)
            const user = this.selections.user_id?.user_full_name
            const build = (p) => {
                const filter = {
                    type: this.type,
                    id: uniqueId,
                    restrict: true,
                    report: false,
                    params: this.$_.pickBy({
                        course_id: this.selections.course_id?.map(x => x.course_id),
                        period: p,
                        user_id: this.selections.user_id?.user_id,
                    }, x => !this.$_.isBlank(x)),
                }
                Object.defineProperties(filter, {
                    editor: { value: this },
                    editorParams: { value: { selections: this.selections ? { ...this.selections } : null } },
                    chipText: { value: name },
                    tooltipData: { value: this.$_.pickBy({ Course: name, Period: p.join(', '), Teacher: user }, x => !this.$_.isBlank(x)) },
                })
                return filter
            }
            if (period && (period[0] === 'All Periods' || !period.length)) {
                return this.loadPeriods().then(p => build(p.map(x => x.period)))
            }
            return Promise.resolve(build(period))
        },
    }
}
</script>

<style lang="scss" scoped>
.course-panel-inline-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    gap: 16px 24px;
    width: 100%;
    align-items: start;
    padding-bottom: 8px;
}
.course-panel-inline-cell {
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
}
</style>
